import { animate, style, transition, trigger } from '@angular/animations';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip, tap } from 'rxjs/operators';

import { AppSharedModule } from 'app/app-shared.module';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { ReportService, TaxonomyNode } from '../../../../providers/storage-api/report.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { SelectedSection } from '../section-selector/section-selector.component';

export interface TaxonomyFlatTreeNode {
  id: string;
  taxonomyFullPath: string;
  htmlId: string;
  name: string;
  level: number;
  expandable: boolean;
  count: number;
}

class RandomAccessTreeControl<T> extends FlatTreeControl<T> {
  expandParents(node: T, expandedNodes) {
    const parent = this.getParent(node);

    if (parent) {
      this.expand(parent);

      if (this.isExpanded(parent)) {
        expandedNodes.add(parent);
      }

      if (this.getLevel(parent) > 0) {
        this.expandParents(parent, expandedNodes);
      }
    }

    return parent;
  }

  getParent(node: T) {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
  }

  getDirectChildren(node: T) {
    const children = [];

    if (this.dataNodes) {
      const currentLevel = this.getLevel(node);
      let startIndex = this.dataNodes.indexOf(node) + 1;
      let level;

      while (
        startIndex < this.dataNodes.length &&
        (level = this.getLevel(this.dataNodes[startIndex++])) > currentLevel
      ) {
        if (level === currentLevel + 1) {
          children.push(this.dataNodes[startIndex - 1]);
        }
      }
    }

    return children;
  }
}

@EnableClassTelemetry()
@Component({
  selector: 'app-taxonomy-picker',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule],
  templateUrl: './taxonomy-picker.component.html',
  styleUrls: ['./taxonomy-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('searchControlTrigger', [
      transition(':enter', [
        style({ maxWidth: 0 }),
        animate('0.2s linear', style({ maxWidth: '40px' })),
      ]),
      transition(':leave', [animate('0.2s linear', style({ width: 0 }))]),
    ]),
  ],
})
export class TaxonomyPickerComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly selectMultiple = input<boolean>(false);
  readonly showUncategorized = input<boolean>(false);
  readonly showRevert = input(true);
  readonly showDocumentCount = input<boolean>(false);
  readonly query = input<string>();
  readonly selectedNodeIds = input<string[]>([]);
  readonly disabledNodeIds = input<string[]>([]);
  readonly projectId = input<string>();
  readonly widgetDataSource = input<WidgetDataSource>();
  readonly selectedSections = input<SelectedSection[]>();

  readonly selectionChange = output<{ id: string; name: string }[]>();

  readonly taxonomyTreeScroll = viewChild<ElementRef>('taxonomyTreeScroll');

  private readonly reportService = inject(ReportService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly search$ = new Subject<string>();
  readonly currentSearchIndex = signal(-1);
  readonly searchMatches = signal<TaxonomyFlatTreeNode[]>([]);
  readonly searchValue = signal<string>('');

  readonly canRevert = signal(false);
  readonly canCollapse = computed(() => this.expandedNodes().size > 0);

  readonly isLoading = signal(false);

  readonly selectedNodes = signal(new Set<TaxonomyFlatTreeNode>());
  readonly expandedNodes = signal(new Set<TaxonomyFlatTreeNode>());

  private originalSelectedNodeIds: string[] = [];

  treeControl = new RandomAccessTreeControl<TaxonomyFlatTreeNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener<any, TaxonomyFlatTreeNode>(
    (node: any, level: number) => {
      return {
        expandable: !!node.children && node.children.length > 0,
        id: node.id,
        name: node.name,
        level,
        taxonomyFullPath: node.taxonomyFullPath,
        htmlId: node.taxonomyFullPath.replace(/[^a-zA-Z0-9\-_]/g, '_'),
        count: node.docCount,
      };
    },
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: TaxonomyFlatTreeNode) => node.expandable;

  constructor() {
    toObservable(this.selectedNodeIds)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this.initializeSelection();
        this.canRevert.set(false);
      });

    toObservable(this.query)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this.loadTaxonomyReport();
      });

    toObservable(this.selectedSections)
      .pipe(
        skip(1),
        takeUntilDestroyed(),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr))
      )
      .subscribe(() => {
        this.loadTaxonomyReport();
      });
  }

  ngOnInit() {
    this.loadTaxonomyReport();

    this.search$
      .pipe(
        debounceTime(250),
        tap((val) => this.searchValue.set(val)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((val) => {
        if (val) {
          if (this.currentSearchIndex() === -1) {
            const searchString = val.toUpperCase();
            const matches = (this.dataSource as any)._flattenedData.value.filter((n) =>
              n.name.toUpperCase().includes(searchString)
            );
            this.searchMatches.set(matches);

            if (matches.length) {
              this.currentSearchIndex.set(0);
            }
          }

          this.handleSearchResult();
        }
      });
  }

  private async loadTaxonomyReport() {
    this.isLoading.set(true);

    const taxonomyReport = await this.reportService.getTaxonomyNodesReport(
      this.widgetDataSource(),
      this.projectId(),
      this.query(),
      this.timezoneService.timezone,
      this.selectedSections()
    );

    const taxonomyNodes: Record<string, TaxonomyNode> = taxonomyReport.items.nodes;
    const addChildToParent = (parent: TaxonomyNode, child: TaxonomyNode): void => {
      parent.children ??= [];
      const childExists: boolean = parent.children.some(
        (existingChild: TaxonomyNode) => existingChild.taxonomyFullPath === child.taxonomyFullPath
      );
      if (!childExists) {
        parent.children.push(child);
        parent.children.sort((a: TaxonomyNode, b: TaxonomyNode) => a.name.localeCompare(b.name));
      }
    };
    const buildMissingParents = (
      child: TaxonomyNode,
      nodes: Record<string, TaxonomyNode>
    ): void => {
      let currentChild: TaxonomyNode = child;
      let parentPath: string = child.path;
      while (parentPath) {
        let parentNode: TaxonomyNode = nodes[parentPath];
        if (!parentNode) {
          const parentName: string = parentPath.split('|').pop() || parentPath;
          const parentParentPath: string = parentPath.includes('|')
            ? parentPath.substring(0, parentPath.lastIndexOf('|'))
            : '';
          parentNode = {
            id: undefined,
            taxonomyFullPath: parentPath,
            name: parentName,
            path: parentParentPath,
            docCount: undefined,
            leaf: false,
            children: [],
          };
          nodes[parentPath] = parentNode;
        }
        addChildToParent(parentNode, currentChild);
        currentChild = parentNode;
        parentPath = parentNode.path;
      }
    };
    Object.keys(taxonomyNodes).forEach((id) => {
      const curr: TaxonomyNode = taxonomyNodes[id];
      if (!curr.path) {
        return;
      }
      if (curr.taxonomyFullPath.includes('|')) {
        buildMissingParents(curr, taxonomyNodes);
      }
      const parent: TaxonomyNode = taxonomyNodes[curr.path];
      if (parent) {
        addChildToParent(parent, curr);
      }
    });

    const data = Object.values(taxonomyNodes)
      .filter((n) => !n.path)
      .sort((a, b) => a.name.localeCompare(b.name));

    const uncategorizedIdx = data.findIndex((n) => n.taxonomyFullPath === 'UNCATEGORIZED');
    if (uncategorizedIdx > -1) {
      const uncategorized = data.splice(uncategorizedIdx, 1);
      if (this.showUncategorized()) {
        data.unshift(uncategorized[0]);
      }
    }

    this.dataSource.data = data;
    this.isLoading.set(false);

    this.initializeSelection();
  }

  private initializeSelection() {
    let firstNode;
    const newSelected = new Set<TaxonomyFlatTreeNode>();
    const newExpanded = new Set(this.expandedNodes());
    this.originalSelectedNodeIds = [...(this.selectedNodeIds() || [])];
    this.originalSelectedNodeIds.forEach((id) => {
      const flatTreeNode = this.treeControl.dataNodes?.find((n) => n.taxonomyFullPath === id);

      if (flatTreeNode) {
        if (!firstNode || firstNode.name.localeCompare(flatTreeNode.name) > 0) {
          firstNode = flatTreeNode;
        }

        newSelected.add(flatTreeNode);
        this.treeControl.expandParents(flatTreeNode, newExpanded);
      }
    });
    this.selectedNodes.set(newSelected);
    this.expandedNodes.set(newExpanded);
    this.scrollToNode(firstNode);
  }

  onSearchChanged($event) {
    this.searchMatches.set([]);
    this.currentSearchIndex.set(-1);

    this.search$.next($event);
  }

  searchPrevious() {
    this.currentSearchIndex.update((i) => (i <= 0 ? this.searchMatches().length - 1 : i - 1));
    this.handleSearchResult();
  }

  searchNext() {
    this.currentSearchIndex.update((i) => (i >= this.searchMatches().length - 1 ? 0 : i + 1));
    this.handleSearchResult();
  }

  private handleSearchResult() {
    const matches = this.searchMatches();
    if (matches.length) {
      const item = matches[this.currentSearchIndex()];
      const newExpanded = new Set(this.expandedNodes());
      this.treeControl.expandParents(item, newExpanded);
      this.expandedNodes.set(newExpanded);
      this.scrollToNode(item);
    }
  }

  private scrollToNode(node: TaxonomyFlatTreeNode) {
    if (node) {
      setTimeout(() => {
        if (this.taxonomyTreeScroll()) {
          const element = document.getElementById(`t-${node.htmlId}`);
          element?.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }

  clearSelection() {
    const restored = new Set<TaxonomyFlatTreeNode>();
    this.originalSelectedNodeIds.forEach((id) => {
      const node = this.treeControl.dataNodes.find((n) => n.taxonomyFullPath === id);
      if (node) {
        restored.add(node);
      }
    });
    this.selectedNodes.set(restored);
    this.handleSelection();
  }

  collapseAll() {
    this.expandedNodes.set(new Set());
    this.treeControl.collapseAll();
  }

  canToggleNodeSelection(node: TaxonomyFlatTreeNode): boolean {
    const selected = this.selectedNodes();
    return (
      !!node.id &&
      !this.disabledNodeIds().includes(node.taxonomyFullPath) &&
      (this.selectMultiple() || !selected.size || selected.has(node))
    );
  }

  toggleNodeSelection($event, node: TaxonomyFlatTreeNode) {
    this.selectedNodes.update((prev) => {
      const next = new Set(prev);
      if ($event.checked) {
        next.add(node);
      } else {
        next.delete(node);
      }
      return next;
    });
    this.handleSelection();
  }

  hasSelectedChild(node: TaxonomyFlatTreeNode): boolean {
    const children = this.treeControl.getDirectChildren(node);
    const selected = this.selectedNodes();
    return children.some((c) => selected.has(c));
  }

  canSelectChildren(node: TaxonomyFlatTreeNode): boolean {
    const children = this.treeControl.getDirectChildren(node);
    const selected = this.selectedNodes();
    return (
      (this.selectMultiple() && children.length && children.some((c) => !selected.has(c))) ||
      (!this.selectMultiple() && children.length === 1 && !selected.size)
    );
  }

  selectAllChildren(node: TaxonomyFlatTreeNode) {
    const disabled = this.disabledNodeIds();
    const children = this.treeControl.getDirectChildren(node);
    this.selectedNodes.update((prev) => {
      const next = new Set(prev);
      children.forEach((c) => {
        if (!!c.id && !disabled.includes(c.taxonomyFullPath)) {
          next.add(c);
        }
      });
      return next;
    });
    this.treeControl.expand(node);
    this.expandedNodes.update((prev) => {
      const next = new Set(prev);
      next.add(node);
      return next;
    });
    this.handleSelection();
  }

  canDeselectChildren(node: TaxonomyFlatTreeNode): boolean {
    const children = this.treeControl.getDirectChildren(node);
    const selected = this.selectedNodes();
    return children.some((c) => selected.has(c));
  }

  deselectAllChildren(node: TaxonomyFlatTreeNode) {
    const disabled = this.disabledNodeIds();
    const children = this.treeControl.getDirectChildren(node);
    this.selectedNodes.update((prev) => {
      const next = new Set(prev);
      children.forEach((c) => {
        if (!!c.id && !disabled.includes(c.taxonomyFullPath)) {
          next.delete(c);
        }
      });
      return next;
    });
    this.handleSelection();
  }

  private handleSelection() {
    const selected = this.selectedNodes();
    const disabled = this.disabledNodeIds();
    const selection = Array.from(selected, (n) => ({
      id: n.taxonomyFullPath,
      name: n.name,
    }));
    const visibleOriginalSelectedNodeIds = this.originalSelectedNodeIds.filter((id) =>
      this.treeControl.dataNodes.some((n) => n.taxonomyFullPath === id)
    );

    this.canRevert.set(
      !visibleOriginalSelectedNodeIds.every((id) => selection.some((s) => s.id === id)) ||
        visibleOriginalSelectedNodeIds.length !== selected.size
    );

    this.selectionChange.emit(selection.filter((n) => !disabled.includes(n.id)));
  }

  toggleNodeExpansion(node: TaxonomyFlatTreeNode) {
    this.treeControl.toggle(node);
    this.expandedNodes.update((prev) => {
      const next = new Set(prev);
      if (this.treeControl.isExpanded(node)) {
        next.add(node);
      } else {
        next.delete(node);
      }
      return next;
    });
  }
}
